import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellContext, ColumnHelper, defineColumns, TanstackTable } from '@thingslog/ui-components';
import clsx from 'clsx';
import { format } from 'date-fns';
import { ScheduledEventsLog, EventExecutionStatus } from '@thingslog/repositories';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const ScheduledTasksHistoryTable: React.FC<ScheduledTasksHistoryTableProps> = ({
  rows,
  isLoading,
}: ScheduledTasksHistoryTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const statusTranslations = useMemo<Record<EventExecutionStatus, string>>(
    () => ({
      DONE: t('scheduled_tasks_history_task_status_done'),
      PENDING: t('scheduled_tasks_history_task_status_pending'),
      FAILED: t('scheduled_tasks_history_task_status_failed'),
      DONE_WITH_ERRORS: t('scheduled_tasks_history_task_status_done_with_errors'),
    }),
    [t]
  );

  const columns = useMemo(
    () =>
      defineColumns<ScheduledEventsLog>((column: ColumnHelper<ScheduledEventsLog>) => {
        const boldHeader = (label: string): ReactNode => (
          <div className="font-semibold">{label}</div>
        );

        return [
          column.accessor('executionTime', {
            header: (): ReactNode =>
              boldHeader(t('scheduled_tasks_history_date_of_execution_header')),
            meta: {
              filterVariant: 'date',
              label: t('scheduled_tasks_history_date_of_execution_header'),
              grow: true,
              minWidth: 110,
            },
            cell: (info: CellContext<ScheduledEventsLog, Date>): ReactNode => {
              const value = info.getValue();
              return value ? format(new Date(value), 'HH:mm dd/MM/yyyy') : '';
            },
          }),
          column.accessor(
            (row: ScheduledEventsLog): string => statusTranslations[row.taskExecutionEnum],
            {
              id: 'taskExecutionEnum',
              header: (): ReactNode => boldHeader(t('scheduled_tasks_history_status_header')),
              meta: {
                filterVariant: 'multiSelect',
                label: t('scheduled_tasks_history_status_header'),
                grow: true,
                minWidth: 150,
              },
              cell: (info: CellContext<ScheduledEventsLog, string>): ReactNode => {
                const status = info.getValue();
                return (
                  <div
                    className={clsx(
                      'rounded border border-solid px-2 py-0.5 text-sm font-bold',
                      status === statusTranslations.DONE
                        ? 'border-emerald-500 text-emerald-500 bg-emerald-50'
                        : status === statusTranslations.PENDING ||
                          status === statusTranslations.DONE_WITH_ERRORS
                        ? 'border-yellow-600 text-yellow-600 bg-yellow-50'
                        : status === statusTranslations.FAILED &&
                          'border-red-500 text-red-500 bg-red-50'
                    )}
                  >
                    {status}
                  </div>
                );
              },
            }
          ),
          column.display({
            id: 'overview',
            header: '',
            meta: { align: 'right', minWidth: 50 },
            cell: (info: CellContext<ScheduledEventsLog, unknown>): ReactNode => (
              <Button
                className="rounded mr-4 p-0"
                size="small"
                variant="outlined"
                onClick={(): void =>
                  navigate(
                    `/taskScheduler/history/${info.row.original.scheduledEventId}/${info.row.original.id}`
                  )
                }
              >
                {t('scheduled_tasks_history_overview_btn_text')}
              </Button>
            ),
          }),
        ];
      }),
    [t, statusTranslations, navigate]
  );

  return (
    <TanstackTable<ScheduledEventsLog>
      mode="client"
      className="w-full"
      classNames={{ container: 'max-h-[calc(100dvh_-_10rem)]' }}
      data={rows}
      columns={columns}
      isLoading={isLoading}
      getRowId={(row: ScheduledEventsLog): string => `${row.id}`}
      density="compact"
      pageSizeOptions={[10, 25, 50]}
      initialState={{
        sorting: [{ id: 'executionTime', desc: true }],
        pagination: { pageIndex: 0, pageSize: 10 },
      }}
      emptyMessage={t('scheduled_tasks_history_no_data', 'No execution history')}
    />
  );
};

interface ScheduledTasksHistoryTableProps {
  rows: ScheduledEventsLog[];
  isLoading?: boolean;
}

export default ScheduledTasksHistoryTable;
